import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { MovimentacaoEstoque } from '../../types';
import { ArrowsRightLeftIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MovimentacaoList: React.FC = () => {
  const navigate = useNavigate();
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoEstoque[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMovimentacoes();
  }, []);

  const loadMovimentacoes = async () => {
    try {
      setLoading(true);
      const response = await estoqueApi.getMovimentacoes();
      setMovimentacoes(response.results || []);
    } catch (err) {
      setError('Erro ao carregar movimentações');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta movimentação?')) {
      return;
    }

    try {
      await estoqueApi.cancelarMovimentacao(id);
      loadMovimentacoes();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao cancelar movimentação');
    }
  };

  const filteredMovimentacoes = movimentacoes.filter(mov =>
    mov.material_codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mov.material_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTipoBadge = (tipo: string, tipoDisplay?: string) => {
    if (tipo === 'E') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ↓ {tipoDisplay || 'Entrada'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        ↑ {tipoDisplay || 'Saída'}
      </span>
    );
  };

  const getStatusBadge = (status: string, statusDisplay?: string) => {
    const colors: Record<string, string> = {
      P: 'bg-yellow-100 text-yellow-800',
      C: 'bg-green-100 text-green-800',
      R: 'bg-blue-100 text-blue-800',
      CA: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusDisplay || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-gray-900">Movimentações de Estoque</h1>
          <p className="text-xs text-gray-600">Histórico de entradas e saídas</p>
        </div>
        <button
          onClick={() => navigate('/estoque/movimentacoes/nova')}
          className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Nova Movimentação
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Busca */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <input
          type="text"
          placeholder="Buscar por código ou nome do material..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Material
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantidade
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preço Unit.
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor Total
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredMovimentacoes.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-sm text-gray-500">
                  Nenhuma movimentação encontrada
                </td>
              </tr>
            ) : (
              filteredMovimentacoes.map((mov) => (
                <tr key={mov.id} className="hover:bg-gray-25">
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs text-gray-900">{formatDate(mov.data)}</div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {getTipoBadge(mov.tipo, mov.tipo_display)}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="text-xs font-medium text-gray-900">{mov.material_codigo}</div>
                    <div className="text-xs text-gray-500">{mov.material_nome}</div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    <div className="text-xs font-semibold text-gray-900">
                      {mov.quantidade} {mov.material_unidade}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="text-xs text-gray-900">
                      {formatCurrency(mov.preco_unitario)}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="text-xs font-semibold text-gray-900">
                      {formatCurrency(mov.valor_total || 0)}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    {getStatusBadge(mov.status, mov.status_display)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="flex justify-end space-x-1">
                      {mov.status === 'C' && (
                        <button
                          onClick={() => handleCancelar(Number(mov.id))}
                          className="text-red-600 hover:text-red-900 p-0.5"
                          title="Cancelar"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-600">
          Total: {filteredMovimentacoes.length} movimentação(ões)
        </div>
      </div>
    </div>
  );
};

export default MovimentacaoList;
